import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getApplication, updateSnapshot, resend } from '../api/admin'
import type { AdminApplicationDetail, AdminSubmissionAttempt } from '../api/adminTypes'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { StatusBadge } from './StatusBadge'
import { formatDateTime, formatBytes } from './format'
import { LoadingNote, ErrorNote } from './Notes'
import { useAdminSession } from './session'

// Real Salesforce pushes are a sequential chain (Lead lookup, Account,
// Contact, Application_Form__c, owners, directors, document attachments) —
// commonly 15-20+ seconds, observed as long as ~17s against the real
// sandbox. A short fixed setTimeout before reloading (the original
// approach) reliably shows a stale "in flight" attempt; poll until the new
// attempt actually has a finished_at instead of guessing a delay.
const RESEND_POLL_INTERVAL_MS = 3000
const RESEND_POLL_TIMEOUT_MS = 90000

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAdminSession()
  const [app, setApp] = useState<AdminApplicationDetail | null>(null)
  const [error, setError] = useState(false)

  function load() {
    if (!id) return Promise.resolve(undefined)
    return getApplication(id)
      .then((fresh) => {
        setApp(fresh)
        return fresh
      })
      .catch(() => {
        setError(true)
        return undefined
      })
  }

  useEffect(() => {
    setApp(null)
    setError(false)
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (error) return <ErrorNote>Application not found, or something went wrong loading it.</ErrorNote>
  if (!app) return <LoadingNote>Loading application…</LoadingNote>

  return (
    <div>
      <Link to="/applications" style={{ color: 'var(--color-action-primary)', fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', textDecoration: 'none' }}>
        ‹ Applications
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap', margin: 'var(--spacing-3) 0 var(--spacing-6) 0' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-lg)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {app.account_email}
        </h1>
        <StatusBadge status={app.status} />
        {app.sf_state && <StatusBadge status={app.sf_state} />}
      </div>

      <Card padding="md" style={{ marginBottom: 'var(--spacing-6)' }}>
        <DetailRow label="Form type" value={app.form_type.toUpperCase()} />
        <DetailRow label="SF reference" value={app.sf_reference ?? '—'} />
        <DetailRow label="Attempts" value={String(app.attempts_count)} />
        <DetailRow label="Created" value={formatDateTime(app.created_at)} />
        <DetailRow label="Submitted" value={formatDateTime(app.submitted_at)} />
        <DetailRow label="Last updated" value={formatDateTime(app.last_updated_at)} last />
      </Card>

      <SectionHeading>Salesforce activity ({app.attempts.length})</SectionHeading>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--spacing-3) 0' }}>
        Every push attempt, step by step. Expand a step to see its exact request/response — this is
        where a failure's real error message lives.
      </p>
      {app.attempts.length === 0 && (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-6)' }}>
          No Salesforce push has run for this application yet.
        </p>
      )}
      {app.attempts.map((attempt) => (
        <AttemptCard key={attempt.id} attempt={attempt} />
      ))}

      <SectionHeading>Documents ({app.documents.length})</SectionHeading>
      <Card padding={false} style={{ overflow: 'hidden', marginBottom: 'var(--spacing-6)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-outline-variant)' }}>
              <Th>Filename</Th>
              <Th>Slot</Th>
              <Th>Size</Th>
              <Th>Upload status</Th>
              <Th>SF attachment</Th>
              <Th>Uploaded</Th>
            </tr>
          </thead>
          <tbody>
            {app.documents.length === 0 && (
              <tr><Td colSpan={6}>No documents.</Td></tr>
            )}
            {app.documents.map((d) => (
              <tr key={d.id} style={{ borderBottom: '1px solid var(--color-outline-subtle)' }}>
                <Td>{d.filename}</Td>
                <Td>{d.slot}{d.slot_index != null ? ` #${d.slot_index}` : ''}</Td>
                <Td>{formatBytes(d.size_bytes)}</Td>
                <Td><StatusBadge status={d.upload_status} /></Td>
                <Td>{d.sf_attachment_status ? <StatusBadge status={d.sf_attachment_status} /> : '—'}</Td>
                <Td>{formatDateTime(d.uploaded_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <SectionHeading>Form snapshot</SectionHeading>
      {app.form_snapshot && app.status === 'submitted' && user?.role === 'admin' ? (
        <SnapshotEditor application={app} onSaved={load} />
      ) : (
        <Card padding="md">
          <pre
            style={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-primary)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '400px',
              overflow: 'auto',
            }}
          >
            {app.form_snapshot ? JSON.stringify(app.form_snapshot, null, 2) : 'No snapshot — draft not yet submitted.'}
          </pre>
        </Card>
      )}
    </div>
  )
}

// Edit & Resend (admin-console.md's "Edit & resubmit"): a raw-JSON editor
// over the whole snapshot, deliberately whole-document replace rather than a
// field-by-field form (Phase 1 — see UpdateApplicationSnapshotForAdmin's
// comment) plus a resend action, kept in one component since the two are
// almost always used together (fix the bad value, then push the fix).
function SnapshotEditor({
  application,
  onSaved,
}: {
  application: AdminApplicationDetail
  onSaved: () => Promise<AdminApplicationDetail | undefined>
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirmingResend, setConfirmingResend] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [resendStatus, setResendStatus] = useState<'idle' | 'waiting' | 'done' | 'timed-out'>('idle')

  function startEditing() {
    setText(JSON.stringify(application.form_snapshot, null, 2))
    setParseError(null)
    setSaveError(null)
    setEditing(true)
  }

  async function handleSave() {
    let parsed: Record<string, unknown>
    try {
      const value = JSON.parse(text)
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        setParseError('Must be a JSON object, not an array or primitive.')
        return
      }
      parsed = value
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON.')
      return
    }
    setParseError(null)
    setSaving(true)
    setSaveError(null)
    try {
      await updateSnapshot(application.id, parsed)
      setEditing(false)
      onSaved()
    } catch {
      setSaveError('Save failed. The application may no longer be editable, or something went wrong.')
    } finally {
      setSaving(false)
    }
  }

  async function handleResend() {
    setResending(true)
    setResendError(null)
    const attemptsBefore = application.attempts.length
    try {
      await resend(application.id)
      setConfirmingResend(false)
      setResendStatus('waiting')

      const deadline = Date.now() + RESEND_POLL_TIMEOUT_MS
      let settled = false
      while (Date.now() < deadline) {
        await sleep(RESEND_POLL_INTERVAL_MS)
        const fresh = await onSaved()
        if (!fresh) continue
        const latest = fresh.attempts[fresh.attempts.length - 1]
        if (fresh.attempts.length > attemptsBefore && latest?.finished_at) {
          settled = true
          break
        }
      }
      setResendStatus(settled ? 'done' : 'timed-out')
    } catch {
      setResendError('Could not queue the resend. Please try again.')
      setResendStatus('idle')
    } finally {
      setResending(false)
    }
  }

  return (
    <Card padding="md">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-3)' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Edit the full snapshot below, save, then resend. Saving does not resend by itself.
        </p>
        {!editing && (
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <Button variant="outline" size="sm" onClick={startEditing}>
              Edit snapshot
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmingResend(true)} disabled={resending}>
              Resend to Salesforce
            </Button>
          </div>
        )}
      </div>

      {resendStatus === 'waiting' && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
          Push in progress — this usually takes 15–30 seconds. Checking for the result…
        </p>
      )}
      {resendStatus === 'done' && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-success-text)', marginBottom: 'var(--spacing-3)' }}>
          Resend finished — see the new attempt in Salesforce activity above.
        </p>
      )}
      {resendStatus === 'timed-out' && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-warning-text)', marginBottom: 'var(--spacing-3)' }}>
          Still running after 90 seconds — refresh the page in a bit to check the result.
        </p>
      )}
      {resendError && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-error-text)', marginBottom: 'var(--spacing-3)' }}>
          {resendError}
        </p>
      )}

      {editing ? (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: '400px',
              fontFamily: 'monospace',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-primary)',
              background: 'var(--color-surface-container-low)',
              border: `1px solid ${parseError ? 'var(--color-error-border)' : 'var(--color-outline-variant)'}`,
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-3)',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
          {parseError && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-error-text)', margin: 'var(--spacing-2) 0' }}>
              {parseError}
            </p>
          )}
          {saveError && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-error-text)', margin: 'var(--spacing-2) 0' }}>
              {saveError}
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)' }}>
            <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
              Save
            </Button>
            <Button variant="tertiary" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-primary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '400px',
            overflow: 'auto',
          }}
        >
          {JSON.stringify(application.form_snapshot, null, 2)}
        </pre>
      )}

      {confirmingResend && (
        <ConfirmResendDialog
          onCancel={() => setConfirmingResend(false)}
          onConfirm={handleResend}
          resending={resending}
        />
      )}
    </Card>
  )
}

// This is a real, irreversible Salesforce write (legacy parity: no upsert,
// always a new Application_Form__c — salesforce-contract.md § Resend
// semantics), so it gets an explicit confirm step rather than acting on a
// single click.
function ConfirmResendDialog({
  onCancel,
  onConfirm,
  resending,
}: {
  onCancel: () => void
  onConfirm: () => void
  resending: boolean
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 11, 69, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--spacing-6)',
      }}
    >
      <Card padding="lg" style={{ maxWidth: '440px' }}>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-sm)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          Resend to Salesforce?
        </h3>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-6) 0',
          }}
        >
          This creates a new Application_Form__c record in Salesforce using the current snapshot. It
          does not update or replace the previous attempt — this cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
          <Button variant="tertiary" onClick={onCancel} disabled={resending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={resending}>
            Resend
          </Button>
        </div>
      </Card>
    </div>
  )
}

function AttemptCard({ attempt }: { attempt: AdminSubmissionAttempt }) {
  const [expanded, setExpanded] = useState(attempt.status === 'failed')
  const steps = Object.keys(attempt.sf_response_log ?? {})

  return (
    <Card padding="md" style={{ marginBottom: 'var(--spacing-4)' }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          fontFamily: 'var(--font-body)',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          <strong style={{ color: 'var(--color-text-primary)', fontSize: 'var(--text-body-md)' }}>
            Attempt {attempt.attempt_no}
          </strong>
          <StatusBadge status={attempt.status} />
          <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)' }}>
            by {attempt.actor}
          </span>
        </span>
        <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)' }}>
          {formatDateTime(attempt.started_at)}
          {attempt.finished_at ? ` → ${formatDateTime(attempt.finished_at)}` : ' (in progress)'}
        </span>
      </button>

      {expanded && (
        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {steps.length === 0 && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)' }}>
              No steps logged yet.
            </p>
          )}
          {steps.map((step) => (
            <StepLog
              key={step}
              step={step}
              request={(attempt.sf_request_log as Record<string, unknown>)?.[step]}
              response={(attempt.sf_response_log as Record<string, unknown>)?.[step]}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

// A step's response is considered an error if it carries a "message" or
// "error" field — the same shape application/salesforce.go's errMessageOf
// looks for when it decides whether to append to the attempt's failures list.
function stepFailed(response: unknown): boolean {
  if (!response || typeof response !== 'object') return false
  const r = response as Record<string, unknown>
  return typeof r.message === 'string' || typeof r.error === 'string'
}

function StepLog({ step, request, response }: { step: string; request: unknown; response: unknown }) {
  const failed = stepFailed(response)
  return (
    <div
      style={{
        borderLeft: `3px solid ${failed ? 'var(--color-error-border)' : 'var(--color-outline-variant)'}`,
        paddingLeft: 'var(--spacing-4)',
        marginBottom: 'var(--spacing-4)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: failed ? 'var(--color-error-text)' : 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-1)',
        }}
      >
        {step.replace(/_/g, ' ')} {failed && '— error'}
      </div>
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)' }}>
          request / response
        </summary>
        <pre
          style={{
            margin: '8px 0 0 0',
            fontFamily: 'monospace',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-text-primary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: 'var(--color-surface-container-low)',
            padding: 'var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {JSON.stringify({ request, response }, null, 2)}
        </pre>
      </details>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-title-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-primary)',
        margin: '0 0 var(--spacing-3) 0',
      }}
    >
      {children}
    </h2>
  )
}

function DetailRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: last ? 'none' : '1px solid var(--color-outline-subtle)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-body-sm)',
      }}
    >
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{ padding: '10px 16px', fontSize: 'var(--text-label-sm)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-secondary)' }}>
      {children}
    </th>
  )
}

function Td({ children, colSpan }: { children: React.ReactNode; colSpan?: number }) {
  return (
    <td colSpan={colSpan} style={{ padding: '12px 16px', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)' }}>
      {children}
    </td>
  )
}
